/**
 * SCM15 — per-row BoM status control.
 *
 * Renders the current status pill plus a "▾" affordance that opens a
 * popover offering legal forward transitions (and, in expanded mode, a
 * revert affordance). Activating a transition:
 *
 *   1. Calls `validateStatusTransition` synchronously — if it returns
 *      `invalid`, the dropdown surfaces an inline error and bails.
 *   2. Calls the parent's `onTransition(item, toStatus, opts)` callback
 *      with the validation outcome. The parent owns the persist path
 *      (it writes the new BoM, calls `writeBomStatusChange`, and
 *      handles rollback on failure).
 *
 * The component is purposefully unaware of Firestore — it accepts a
 * read-only `BomItem` + a callback. Tests can exercise it with a stub
 * callback and never touch the network.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, RotateCcw } from 'lucide-react';
import type { BomItem, BomItemStatus } from '../../../types/supplyChain';
import {
  BOM_STATUS_ORDER,
  STATUS_LABELS,
  STATUS_ORDINAL,
  validateStatusTransition,
} from '../../../lib/bomItemStatusFlow';
import { cn } from '../../../lib/utils';

export interface BomStatusControlProps {
  item: BomItem;
  /** Disabled when no write surface is wired (e.g. read-only previews). */
  disabled?: boolean;
  /** Called when the user picks a new status. The parent owns the persist path. */
  onTransition?: (
    item: BomItem,
    toStatus: BomItemStatus,
    opts: { revert: boolean; reason?: string },
  ) => void;
  /** Optional tone override for the pill — defaults to the parent-derived tone. */
  toneClass?: string;
}

/**
 * Status pill rendered when the control is read-only. Mirrors the
 * inline pill the BoM panel used before SCM15 so the visual surface
 * stays identical when the control is disabled.
 */
function ReadonlyPill({ status, toneClass }: { status: BomItemStatus; toneClass?: string }) {
  return (
    <span
      className={cn(
        'px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest',
        toneClass,
      )}
      data-testid="scm-bom-status-readonly"
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

export const BomStatusControl: React.FC<BomStatusControlProps> = ({
  item,
  disabled = false,
  onTransition,
  toneClass,
}) => {
  const [open, setOpen] = useState(false);
  const [showRevert, setShowRevert] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click. The popover is small + transient — local state
  // is fine.
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowRevert(false);
        setError(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentOrdinal = STATUS_ORDINAL[item.status];

  const forwardChoices = useMemo(
    () => BOM_STATUS_ORDER.filter((s) => STATUS_ORDINAL[s] > currentOrdinal),
    [currentOrdinal],
  );
  const revertChoices = useMemo(
    () => BOM_STATUS_ORDER.filter((s) => STATUS_ORDINAL[s] < currentOrdinal),
    [currentOrdinal],
  );

  if (disabled || !onTransition) {
    return <ReadonlyPill status={item.status} toneClass={toneClass} />;
  }

  const handlePick = (toStatus: BomItemStatus, revert: boolean) => {
    const v = validateStatusTransition(item.status, toStatus, { revert });
    if (v.kind === 'invalid') {
      setError(v.reason);
      return;
    }
    if (v.kind === 'noop') {
      setOpen(false);
      setShowRevert(false);
      return;
    }
    let reason: string | undefined;
    if (revert) {
      // Reverts require a reason — keep the UX cheap (prompt is fine for
      // an MVP). A nicer modal lands when SCM-Integration owns the write
      // path company-wide.
      const r = window.prompt(
        `Reverting ${STATUS_LABELS[item.status]} → ${STATUS_LABELS[toStatus]}. Please provide a reason:`,
      );
      if (!r || !r.trim()) {
        setError('Revert requires a reason.');
        return;
      }
      reason = r.trim();
    }
    onTransition(item, toStatus, { revert, reason });
    setOpen(false);
    setShowRevert(false);
    setError(null);
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest border transition-colors',
          toneClass,
          'border-transparent hover:border-border',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`scm-bom-status-toggle-${item.id}`}
      >
        {STATUS_LABELS[item.status]}
        <ChevronDown className="w-2.5 h-2.5" aria-hidden="true" />
      </button>
      {open && (
        <div
          className="absolute z-20 mt-1 right-0 w-52 bg-background border border-border shadow-lg p-1 space-y-0.5"
          role="menu"
          data-testid={`scm-bom-status-menu-${item.id}`}
        >
          {forwardChoices.length === 0 && !showRevert && (
            <div className="px-2 py-1 type-body text-text-secondary">
              Terminal state — no further transitions.
            </div>
          )}
          {!showRevert &&
            forwardChoices.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handlePick(s, false)}
                className="w-full text-left px-2 py-1 text-[10px] hover:bg-muted/40"
                data-testid={`scm-bom-status-forward-${item.id}-${s}`}
                role="menuitem"
              >
                → {STATUS_LABELS[s]}
              </button>
            ))}
          {showRevert &&
            revertChoices.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handlePick(s, true)}
                className="w-full text-left px-2 py-1 text-[10px] text-warning hover:bg-warning/10"
                data-testid={`scm-bom-status-revert-${item.id}-${s}`}
                role="menuitem"
              >
                ↺ {STATUS_LABELS[s]}
              </button>
            ))}
          {revertChoices.length > 0 && (
            <button
              type="button"
              onClick={() => setShowRevert((v) => !v)}
              className="w-full text-left px-2 py-1 type-body uppercase tracking-widest border-t border-border text-text-secondary hover:text-text-primary inline-flex items-center gap-1"
              data-testid={`scm-bom-status-revert-toggle-${item.id}`}
            >
              <RotateCcw className="w-2.5 h-2.5" />
              {showRevert ? 'Forward steps' : 'Revert…'}
            </button>
          )}
          {error && (
            <div
              className="px-2 py-1 type-body text-danger border-t border-border"
              data-testid={`scm-bom-status-error-${item.id}`}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
